import {
    Component,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';

import { SnackbarService } from '@services/snackbar.service';
import { UserDataService } from '@services/data/user-data.service';
import { DiscrepancyService } from '@services/api/discrepancy/discrepancy.service';
import { LoaderComponent } from '@shared/loader/loader.component';
import { TextButtonComponent } from '@shared/button/text-button/text-button.component';
import { AssignDiscrepancyDialogComponent } from '@main/dialogs/assign-discrepancy-dialog/assign-discrepancy-dialog.component';
import { DiscrepancyEditOptions, DiscrepancyDetail } from '@models/discrepancy';
import { refreshActionRelatedData } from 'app/store/actions/discrepancy.actions';
import { SubscriptionList, SubscriptionListType } from '@helpers/subscription';
import DATA_QA from '@automation/data-qa.json';

@Component({
    selector: 'app-footer-assign-actions',
    template: `
        <div>
            <app-text-button
                (clicked)="takeDiscrepancy()"
                [dataQa]="dataQa.footerTakeDiscrepancyButton"
                [disabled]="!canTake">
                Take
            </app-text-button>
            <app-text-button
                (clicked)="putbackDiscrepancy()"
                [dataQa]="dataQa.footerPutbackDiscrepancyButton"
                [disabled]="!isAssignedToMe">
                Put back
            </app-text-button>
            @if (this.isAdmin) {
                <app-text-button
                    [disabled]="!this.isAdmin"
                    [dataQa]="dataQa.footerAssignDiscrepancyButton"
                    (clicked)="assignDiscrepancy()">
                    Assign
                </app-text-button>
                <app-text-button
                    [disabled]="!canUnassign"
                    [dataQa]="dataQa.footerUnassignDiscrepancyButton"
                    (clicked)="unassignDiscrepancy()">
                    Unassign
                </app-text-button>
            }
        </div>
        @if (loading) {
            <app-loader type="overlay" />
        }
    `,
    imports: [TextButtonComponent, LoaderComponent],
})
export class FooterAssignActionsComponent
    implements OnInit, OnChanges, OnDestroy
{
    @Input({ required: true }) discDetail!: DiscrepancyDetail;
    @Input({ required: true }) editOptions!: DiscrepancyEditOptions;
    @Input({ required: true }) isAdmin!: boolean;
    @Input() canTakeByRoleAndCollaborator = true;
    @Input() isSecondaryCollaborator = false;
    @Input() allowCollaboratorTakeAssignedDiscrepancy = false;

    loading = false;
    isAssignedToMe = false;
    canTake = false;
    canUnassign = false;
    private _subscriptions = new SubscriptionList() as SubscriptionListType;
    readonly dataQa = DATA_QA;

    private readonly store = inject(Store);
    private readonly dialog = inject(MatDialog);
    private readonly userDataService = inject(UserDataService);
    private readonly snackbarService = inject(SnackbarService);
    private readonly discrepancyService = inject(DiscrepancyService);

    ngOnInit(): void {
        this.setActionPermissions();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes['discDetail'] ||
            changes['isAdmin'] ||
            changes['canTakeByRoleAndCollaborator'] ||
            changes['isSecondaryCollaborator'] ||
            changes['allowCollaboratorTakeAssignedDiscrepancy']
        ) {
            this.setActionPermissions();
        }
    }

    ngOnDestroy() {
        this._subscriptions.unsubscribeAllSafe();
    }

    takeDiscrepancy() {
        const discId = this.discDetail?.id;
        if (discId) {
            this.setLoading(true);

            this._subscriptions['take-discrepancy'] = this.discrepancyService
                .takeDiscrepancy(discId)
                .subscribe({
                    next: (success) => {
                        if (success) {
                            this.snackbarService.success({
                                variant: 'success',
                                header: `Discrepancy ${this.discDetail.discrepancyNo} Has Been Taken`,
                            });
                            this.store.dispatch(refreshActionRelatedData());
                        }
                    },
                    complete: () => this.setLoading(false),
                });
        }
    }

    putbackDiscrepancy() {
        const discId = this.discDetail?.id;
        if (discId && this.isAssignedToMe) {
            this.setLoading(true);

            this._subscriptions['putback-discrepancy'] = this.discrepancyService
                .putbackDiscrepancy(discId)
                .subscribe({
                    next: (success) => {
                        if (success) {
                            this.snackbarService.success({
                                variant: 'success',
                                header: `Discrepancy ${this.discDetail.discrepancyNo} Has Been Put Back`,
                            });
                            this.store.dispatch(refreshActionRelatedData());
                        }
                    },
                    complete: () => this.setLoading(false),
                });
        }
    }

    assignDiscrepancy() {
        const discId = this.discDetail?.id;
        this.dialog.open(AssignDiscrepancyDialogComponent, {
            data: {
                id: discId,
                discrepancyNo: this.discDetail.discrepancyNo,
                options: this.editOptions.analyst,
            },
            maxWidth: '450px',
            minWidth: '200px',
            width: '450px',
        });
    }

    unassignDiscrepancy() {
        const discId = this.discDetail?.id;
        if (discId) {
            this.setLoading(true);

            this._subscriptions['unassign-discrepancy'] =
                this.discrepancyService.unassignDiscrepancy(discId).subscribe({
                    next: (success) => {
                        if (success) {
                            this.snackbarService.success({
                                variant: 'success',
                                header: `Discrepancy ${this.discDetail.discrepancyNo} Has Been Unassigned`,
                            });
                            this.store.dispatch(refreshActionRelatedData());
                        }
                    },
                    complete: () => this.setLoading(false),
                });
        }
    }

    private setActionPermissions() {
        const userId = this.userDataService.currentUser?.id;
        this.isAssignedToMe = this.discDetail.analyst?.backingField == userId;
        this.canTake = this.canTakeDiscrepancy();
        this.canUnassign = this.canUassignDiscrepancy();
    }

    private canTakeDiscrepancy() {
        if (!this.canTakeByRoleAndCollaborator) {
            return false;
        }

        const noAnalyst = this.discDetail.analyst === null;

        if (this.isAdmin) {
            return !this.isAssignedToMe;
        }

        if (noAnalyst && !this.isAssignedToMe) {
            return true;
        }

        if (this.allowCollaboratorTakeAssignedDiscrepancy) {
            return !this.isAssignedToMe;
        }

        return false;
    }

    private canUassignDiscrepancy() {
        const noAnalyst = this.discDetail.analyst === null;
        if (this.isAdmin) return !this.isAssignedToMe && !noAnalyst;
        return false;
    }

    private setLoading(value: boolean) {
        this.loading = value;
    }
}
